import { useCallback, useEffect, useRef, useState } from "react";
import { IStationDiagTable } from "../../../../models/IUser";

interface UseModuleStationDiagOptions {
  /** Изделие, чью карточку открыли. Без него читать нечего. */
  moduleId?: number;
  fetchTable?: (moduleId: number) => Promise<IStationDiagTable>;
}

function errorMessage(error: any): string {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Диагностика недоступна"
  );
}

/**
 * `п. 3` резерва: таблица диагностики станций для окна модуля.
 *
 * Данные читаются один раз на открытие карточки: кадр `station_diag` приходит по
 * изменению, и живое обновление здесь ничего не добавляет — окно открыто минуты.
 * Сбой чтения — не беда карточки: показываем причину в блоке, остальное работает.
 */
export function useModuleStationDiag({
  moduleId,
  fetchTable,
}: UseModuleStationDiagOptions) {
  const [table, setTable] = useState<IStationDiagTable | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Функция приходит пропсом и меняет тождество на каждом рендере родителя;
  // держим её в ссылке, чтобы чтение не зациклилось на новом пропсе.
  const fetchRef = useRef(fetchTable);
  useEffect(() => {
    fetchRef.current = fetchTable;
  });

  const load = useCallback(async () => {
    const fetch = fetchRef.current;

    if (!moduleId || !fetch) {
      setTable(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      setTable(await fetch(moduleId));
    } catch (requestError) {
      setTable(null);
      setError(errorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    load();
  }, [load]);

  return { table, isLoading, error, reload: load };
}
